import type { BillingOverview } from '@pos/api-client';
import { formatAmount } from '@pos/business-logic';
import { Badge, ErrorState, ListGroup, ListRow, Notice, Skeleton, type Tone } from '@pos/ui';
import { useQuery } from '@tanstack/react-query';
import { api, useAuth } from '../../app/auth';
import { Screen } from '../../app/Screen';

const STATUS: Record<string, { label: string; tone: Tone }> = {
  TRIAL: { label: 'Пробный период', tone: 'primary' },
  ACTIVE: { label: 'Активна', tone: 'success' },
  PAST_DUE: { label: 'Ожидает оплаты', tone: 'warning' },
  GRACE_PERIOD: { label: 'Льготный период', tone: 'warning' },
  SUSPENDED: { label: 'Приостановлена', tone: 'danger' },
  CANCELLED: { label: 'Отменена', tone: 'neutral' },
  EXPIRED: { label: 'Истекла', tone: 'danger' },
};

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—');
const limit = (m: { used: number; limit: number | null }) => (m.limit === null ? `${m.used}` : `${m.used} из ${m.limit}`);

export function SubscriptionPage() {
  const biz = useAuth((s) => s.businessId);
  const { data: d, isError, refetch } = useQuery({ queryKey: ['billing', biz], queryFn: () => api.get<BillingOverview>('/billing') });
  if (isError)
    return (
      <Screen title="Подписка" back="/more">
        <ErrorState description="Не удалось загрузить подписку." onRetry={() => void refetch()} />
      </Screen>
    );
  if (!d)
    return (
      <Screen title="Подписка" back="/more">
        <Skeleton className="h-64 rounded-lg" />
      </Screen>
    );
  const s = d.subscription;
  const st = s?.status ? STATUS[s.status] : undefined;
  const end = s?.status === 'TRIAL' ? s.trialEndsAt : s?.currentPeriodEnd;
  return (
    <Screen title="Подписка" back="/more">
      {s && (
        <div className="mb-6 rounded-2xl border border-line bg-surface p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xl font-semibold">{s.planName}</p>
            {st && <Badge tone={st.tone}>{st.label}</Badge>}
          </div>
          <p className="mt-1 text-md text-ink-3">
            {s.status === 'TRIAL' ? 'Пробный до' : 'Оплачено до'} {day(end)}
          </p>
          {s.access !== 'full' && <p className="mt-3 text-md font-medium text-danger">Кассы не могут продавать, пока подписка не продлена.</p>}
        </div>
      )}
      <ListGroup title="Использование">
        <ListRow title="Филиалы" trailing={<span className="tnum">{limit(d.usage.branches)}</span>} />
        <ListRow title="Кассы" trailing={<span className="tnum">{limit(d.usage.devices)}</span>} />
        <ListRow title="Сотрудники" trailing={<span className="tnum">{limit(d.usage.users)}</span>} />
        <ListRow title="Товары" trailing={<span className="tnum">{limit(d.usage.products)}</span>} />
      </ListGroup>
      <ListGroup title="Тарифы">
        {d.plans.map((p) => (
          <ListRow
            key={p.code}
            title={
              <span>
                {p.name} {p.code === s?.planCode && <Badge tone="primary">ваш</Badge>}
              </span>
            }
            subtitle={p.description ?? undefined}
            trailing={<span className="font-medium tnum">{formatAmount(p.priceMonthly)}</span>}
          />
        ))}
      </ListGroup>
      {d.onlinePayment.length === 0 && (
        <Notice tone="info">
          Онлайн-оплата пока не подключена. Для продления или смены тарифа свяжитесь с поддержкой
          {d.support.phone ? (
            <>
              : <a href={`tel:${d.support.phone.replace(/[^\d+]/g, '')}`} className="font-medium underline">{d.support.phone}</a>
            </>
          ) : null}
          {d.support.email ? (
            <>
              {d.support.phone ? ', ' : ': '}
              <a href={`mailto:${d.support.email}`} className="font-medium underline">{d.support.email}</a>
            </>
          ) : null}
          .
        </Notice>
      )}
    </Screen>
  );
}
